import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CurrentPatientProvider, DocumentListEvaluation, DocumentListGeneral, DocumentPatient, DocumentProvider, Spinner, } from "@madmedical/ui";
import { adaptPatient, useGetTreatingDoctorsQuery, useMe, usePatient, useRouteNavigate, } from "@madmedical/user";
import { invariant, useDebounce } from "@madmedical/utils";
import { getMetricTypeChoices } from "@madmedical/medical";
import { useCallback, useMemo, useState } from "react";
import { useGetDocumentsQuery } from "./api";
import DeleteDocumentDialog from "./modals/DeleteDocumentDialog";
import UploadModal from "./modals/UploadModal";
import adaptDocument from "./adaptDocument";
var ModalMode;
(function (ModalMode) {
    ModalMode[ModalMode["Closed"] = 0] = "Closed";
    ModalMode[ModalMode["Delete"] = 1] = "Delete";
    ModalMode[ModalMode["Upload"] = 2] = "Upload";
})(ModalMode || (ModalMode = {}));
const Documents = ({ withOpinion }) => {
    var _a, _b;
    const { patientId, isSelfOrRepresented, patient, isLoading: isPatientLoading, } = usePatient();
    const navigate = useRouteNavigate();
    const { data: doctors } = useGetTreatingDoctorsQuery(patientId);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState({ withOpinion });
    const [search, setSearch] = useState();
    const debouncedSearch = useDebounce(search, 300, () => {
        setPage(1);
    });
    const [modalMode, setModalMode] = useState(ModalMode.Closed);
    const [idForModal, setIdForModal] = useState();
    const { data, isLoading } = useGetDocumentsQuery(Object.assign(Object.assign({ patientId }, filter), { withOpinion, search: debouncedSearch, page }));
    const { roles: { isDoctor }, } = useMe();
    const { data: doctorDocumentsData, isLoading: isDoctorDocumentsLoading } = useGetDocumentsQuery({
        patientId,
        page: 1,
        pageSize: 100,
    }, {
        skip: !isDoctor,
    });
    const filterChoices = useMemo(() => {
        var _a;
        return ({
            metricTypes: getMetricTypeChoices("metricTypes" in filter ? filter.metricTypes : []),
            doctors: (_a = doctors === null || doctors === void 0 ? void 0 : doctors.map(({ userId, fullName }) => {
                var _a;
                return ({
                    key: userId,
                    text: fullName,
                    isSelected: "doctorIds" in filter &&
                        !!((_a = filter.doctorIds) === null || _a === void 0 ? void 0 : _a.includes(userId)),
                });
            })) !== null && _a !== void 0 ? _a : [],
        });
    }, [filter, doctors]);
    const handleFilterChange = useCallback((key, selected) => {
        setPage(1);
        // TODO: Why does type inference fail me?
        const diff = key === "metricTypes"
            ? { metricTypes: selected }
            : key === "doctors"
                ? { doctorIds: selected }
                : {};
        setFilter((prevFilter) => (Object.assign(Object.assign({}, prevFilter), diff)));
    }, []);
    const handleSearchChange = useCallback((search) => {
        setSearch(search);
    }, []);
    const handleSortChange = useCallback((sort, order) => {
        setPage(1);
        if (!sort) {
            setFilter((prevFilter) => (Object.assign(Object.assign({}, prevFilter), { sort: undefined, order: undefined })));
            return;
        }
        setFilter((prevFilter) => (Object.assign(Object.assign({}, prevFilter), { sort: sort, // TODO: Better types
            order })));
    }, []);
    const handleEndReached = useCallback(() => {
        if (!data) {
            return;
        }
        const { currentPage, pageCount } = data.pagination;
        if (currentPage >= pageCount) {
            return;
        }
        setPage(currentPage + 1);
    }, [data]);
    if (isLoading || isPatientLoading || isDoctorDocumentsLoading) {
        return _jsx(Spinner, {});
    }
    invariant(data);
    invariant(patient);
    const { items: documents, stats } = data;
    const handleUploadPress = () => {
        navigate("documents_general", {});
        setModalMode(ModalMode.Upload);
    };
    const handleDeletePress = (id) => {
        setIdForModal(id);
        setModalMode(ModalMode.Delete);
    };
    const handleClose = () => {
        setModalMode(ModalMode.Closed);
        setIdForModal(undefined);
        setPage(1);
    };
    // Avoid switching layout components while refetching - trust the data rather than the prop
    const showWithOpinion = (data === null || data === void 0 ? void 0 : data.items.length)
        ? !!(data === null || data === void 0 ? void 0 : data.items[0].opinion)
        : withOpinion;
    const packages = ((_a = patient === null || patient === void 0 ? void 0 : patient.packages) !== null && _a !== void 0 ? _a : []);
    if (patient && packages.some((item) => (item === null || item === void 0 ? void 0 : item.type) === "basic")) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx(DocumentProvider, Object.assign({ value: {
                    documents: isDoctor && doctorDocumentsData
                        ? (_b = doctorDocumentsData === null || doctorDocumentsData === void 0 ? void 0 : doctorDocumentsData.items) === null || _b === void 0 ? void 0 : _b.map(adaptDocument)
                        : documents.map(adaptDocument),
                    onSortChange: handleSortChange,
                    onSearchChange: handleSearchChange,
                    filterChoices,
                    onFilterChange: handleFilterChange,
                    stats,
                } }, { children: _jsx(CurrentPatientProvider, Object.assign({ value: { patient: adaptPatient(patient) } }, { children: isSelfOrRepresented ? (showWithOpinion ? (_jsx(DocumentListEvaluation, { onUploadPress: handleUploadPress, onEndReached: handleEndReached })) : (_jsx(DocumentListGeneral, { onUploadPress: handleUploadPress, onDeletePress: handleDeletePress, onEndReached: handleEndReached }))) : (_jsx(DocumentPatient, { onEndReached: handleEndReached, onUploadPress: handleUploadPress })) })) })), _jsx(DeleteDocumentDialog, { id: idForModal, open: modalMode === ModalMode.Delete, onClose: handleClose }), _jsx(UploadModal, { open: modalMode === ModalMode.Upload, onClose: handleClose })] }));
};
export default Documents;
